// src/pages/StateMaster.js
import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { FaRegEdit, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { CiFilter } from 'react-icons/ci';
import { MdDelete } from 'react-icons/md';
import { BASE_URL } from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const StateMaster = () => {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // ==================== FETCH STATES ====================
  const fetchStates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/state`, {
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to fetch states');
      }

      const { data } = await res.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error');
      setStates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('token')) fetchStates();
  }, []);

  // ==================== VALIDATION ====================
  const validateForm = (data) => {
    const errors = {};
    if (!data.statename?.trim()) errors.statename = 'State Name is required';
    if (!data.zone?.trim()) errors.zone = 'Zone is required';
    if (data.statecode && data.statecode.length > 10) errors.statecode = 'State Code max 10 chars';
    return errors;
  };

  // ==================== CRUD OPERATIONS ====================
  const createState = async (data) => {
    const res = await fetch(`${API_BASE_URL}/state`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to create state');
    }
    return res.json();
  };

  const updateState = async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/state/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update state');
    }
    return res.json();
  };

  const deleteState = async (id) => {
    const res = await fetch(`${API_BASE_URL}/state/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete state');
    }
    return res.json();
  };

  // ==================== MODAL (Add/Edit) ====================
  const openStateModal = async (state = null) => {
    const isEdit = !!state;

    const { value: formData } = await Swal.fire({
      title: isEdit ? 'Edit State' : 'Add State',
      html: `
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
          <div>
            <label class="block font-medium">State Name *</label>
            <input id="statename" class="swal2-input" value="${state?.statename || ''}" required>
          </div>
          <div>
            <label class="block font-medium">Zone *</label>
            <input id="zone" class="swal2-input" value="${state?.zone || ''}" required>
          </div>
          <div>
            <label class="block font-medium">State Code</label>
            <input id="statecode" class="swal2-input" value="${state?.statecode || ''}" maxlength="10">
          </div>
          <div>
            <label class="block font-medium">Status</label>
            <select id="status" class="swal2-select">
              <option value="true" ${state?.status !== false ? 'selected' : ''}>Enabled</option>
              <option value="false" ${state?.status === false ? 'selected' : ''}>Disabled</option>
            </select>
          </div>
        </div>
      `,
      width: '700px',
      showCancelButton: true,
      confirmButtonText: isEdit ? 'Update' : 'Save',
      preConfirm: () => {
        const data = {
          ...(isEdit && { _id: state._id }),
          statename: document.getElementById('statename').value.trim(),
          zone: document.getElementById('zone').value.trim(),
          statecode: document.getElementById('statecode').value.trim(),
          status: document.getElementById('status').value === 'true',
        };

        const errors = validateForm(data);
        if (Object.keys(errors).length > 0) {
          Swal.showValidationMessage(Object.values(errors).join('<br>'));
          return false;
        }
        return data;
      },
    });

    if (formData) {
      try {
        setLoading(true);
        if (isEdit) {
          await updateState(state._id, formData);
          toast.success('State updated!');
        } else {
          await createState(formData);
          toast.success('State added!');
        }
        fetchStates();
      } catch (err) {
        toast.error(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    }
  };

  // ==================== DELETE ====================
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Delete State?',
      text: 'This cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
    });

    if (confirm.isConfirmed) {
      try {
        setLoading(true);
        await deleteState(id);
        toast.success('State removed.');
        fetchStates();
      } catch (err) {
        toast.error(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    }
  };

  // ==================== FILTER & PAGINATION ====================
  const filtered = states.filter(s =>
    s.statename?.toLowerCase().includes(filter.toLowerCase()) ||
    s.zone?.toLowerCase().includes(filter.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>

      <div className="flex">
      <ToastContainer />

        <div className="flex-1 p-6 bg-gray-100 min-h-screen">
          <h2 className="text-2xl font-bold mb-6">State Master</h2>

          <div className="bg-white rounded-xl shadow p-6">
            {/* Toolbar */}
            <div className="flex flex-wrap gap-4 mb-6 justify-between">
              <div className="flex gap-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or zone..."
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    className="pl-10 pr-4 py-2 border rounded-md w-64"
                  />
                  <CiFilter className="absolute left-3 top-3 text-gray-400" />
                </div>
              </div>
              <button
                onClick={() => openStateModal()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add State
              </button>
            </div>

            {/* Table */}
            {loading ? (
              <div className="text-center py-8">Loading states...</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto border-collapse">
                    <thead className="bg-blue-600 text-white">
                      <tr>
                        <th className="p-3 text-left">S.No</th>
                        <th className="p-3 text-left">State Name</th>
                        <th className="p-3 text-left">Zone</th>
                        <th className="p-3 text-left">Code</th>
                        <th className="p-3 text-left">Status</th>
                        <th className="p-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length === 0 ? (
                        <tr><td colSpan="6" className="text-center py-8 text-gray-500">No states found</td></tr>
                      ) : (
                        paginated.map((s, i) => (
                          <tr key={s._id} className="border-b hover:bg-gray-50">
                            <td className="p-3">
                              {filter
                                ? states.findIndex(originalState => originalState._id === s._id) + 1
                                : (currentPage - 1) * pageSize + i + 1}
                            </td>
                            <td className="p-3 font-medium">{s.statename}</td>
                            <td className="p-3">{s.zone}</td>
                            <td className="p-3">{s.statecode || '-'}</td>
                            <td className="p-3">
                              {s.status ? (
                                <FaCheckCircle className="text-green-500 text-lg" />
                              ) : (
                                <FaTimesCircle className="text-red-500 text-lg" />
                              )}
                            </td>
                            <td className="p-3 text-center space-x-2">
                              <button onClick={() => openStateModal(s)} className="text-blue-600 hover:text-blue-800">
                                <FaRegEdit className="w-5 h-5" />
                              </button>
                              <button onClick={() => handleDelete(s._id)} className="text-red-600 hover:text-red-800">
                                <MdDelete className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-6">
                    <div>
                      Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1">Page {currentPage} of {totalPages}</span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StateMaster;